import { Injectable, inject } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, AngularFirestoreDocument, QueryDocumentSnapshot, QuerySnapshot } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';

import app from 'firebase/compat/app';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { TerpecaRanking } from 'src/app/models/ranking.model';
import { NominationId, TerpecaCategory } from 'src/app/models/room.model';
import {
    ApplicantFieldsEditedPayload, ApplicationDenyRequestPayload, ApplicationResubmitRequestPayload,
    ApplicationStatus, NominationPenaltyWaiverPayload, REVIEWER_EDITABLE_FIELDS, TerpecaUser, UserAuditLogEntry, UserAuditLogEntryType,
    isEligibleToNominate
} from 'src/app/models/user.model';
import { normalizeUserFieldsInPlace, trimInPlace } from 'src/app/utils/misc.utils';
import { environment } from 'src/environments/environment';

import { AppComponent } from '../app.component';
import { TerpecaNomination } from '../models/nomination.model';
import { TerpecaQuote, maxQuotesPerYear } from '../models/quote.model';
import { SettingsService } from './settings.service';
import { RoomRestrictedService } from './room-restricted.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  static signedIn = false;

  private settings = inject(SettingsService);
  private rooms = inject(RoomRestrictedService);
  private afAuth = inject(AngularFireAuth);
  private db = inject(AngularFirestore);
  private router = inject(Router);

  currentUser: TerpecaUser;
  user: Observable<TerpecaUser>;
  private firebaseUser: app.User;
  private firestoreDoc: AngularFirestoreDocument<TerpecaUser>;

  constructor() {
    this.user = this.afAuth.authState.pipe(switchMap((firebaseUser: app.User) => {
      this.firebaseUser = firebaseUser;
      AuthService.signedIn = Boolean(firebaseUser);
      if (firebaseUser) {
        this.firestoreDoc = this.userDoc(firebaseUser.uid);
        return this.firestoreDoc.valueChanges().pipe(
          catchError(error => {
            // If there's a permissions error (e.g., document doesn't exist yet or auth not fully propagated),
            // return null so the flow continues. We'll check if the document exists before attempting to create it.
            // eslint-disable-next-line no-console
            console.warn('Error reading user document:', error);
            return of(null);
          })
        );
      } else {
        this.firestoreDoc = null;
        return of(null);
      }
    }));
    this.user.subscribe(async (currentUser: TerpecaUser) => {
      console.log(`current user is ${currentUser?.email || 'unknown'}`);
      this.currentUser = currentUser;
      if (this.firebaseUser && !currentUser) {
        // We have to be super sure that the doc doesn't exist so we don't overwrite good data.
        try {
          const doc = await this.firestoreDoc.ref.get();
          if (!doc.exists) {
            const newUser: TerpecaUser = {
              createTime: <app.firestore.Timestamp>app.firestore.FieldValue.serverTimestamp(),
              uid: this.firebaseUser.uid,
              email: this.firebaseUser.email,
              displayName: this.firebaseUser.displayName,
              appEmail: this.firebaseUser.email,
              realName: this.firebaseUser.displayName,
              isOwner: false,
              status: ApplicationStatus.NONE
            };
            await this.firestoreDoc.set(newUser, { merge: true });
          }
        } catch (error) {
          // If we can't read the document due to permissions, don't try to create it
          // to avoid overwriting existing data. The error is logged but won't break the app.
          // eslint-disable-next-line no-console
          console.warn('Error checking if user document exists, not attempting to create:', error);
        }
      }
    });
  }

  async updateUser(partialUser: Partial<TerpecaUser>) {
    trimInPlace(partialUser);
    normalizeUserFieldsInPlace(partialUser);
    await this.firestoreDoc.update(partialUser);
  }

  async requestUpgrade(partialUser: Partial<TerpecaUser>) {
    await this.updateUser(partialUser);
    const ref = this.firestoreDoc.ref;
    await ref.firestore.runTransaction(async (transaction) => {
      const snap = await transaction.get(ref);
      const data = snap.data();
      if (!data || data.upgradeRequested) {
        return;
      }
      transaction.update(ref, {
        upgradeRequested: true,
        auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.UPGRADE_REQUESTED, null)
      });
    });
  }

  async denyUpgrade(uidToSet: string, payload: ApplicationResubmitRequestPayload) {
    await this.userDoc(uidToSet).update({
      upgradeRequested: <boolean><unknown>app.firestore.FieldValue.delete(),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.UPGRADE_DENIED, payload)
    });
  }

  async addReviewNote(uidToSet: string, note: string) {
    await this.userDoc(uidToSet).update({
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.REVIEW_NOTE_ADDED, note)
    });
  }

  async editApplicantFields(uidToSet: string, existingUser: TerpecaUser, updates: Partial<TerpecaUser>) {
    if (!this.currentUser.isOwner && this.currentUser.status < ApplicationStatus.REVIEWER) {
      return;
    }
    trimInPlace(updates);
    normalizeUserFieldsInPlace(updates);
    const changes: ApplicantFieldsEditedPayload = {};
    const partialUser: Partial<TerpecaUser> = {};
    for (const field of REVIEWER_EDITABLE_FIELDS) {
      const before = String(existingUser[field] ?? '').trim();
      const after = String(updates[field] ?? '').trim();
      if (before === after) {
        continue;
      }
      changes[field] = { before, after };
      if (after === '') {
        partialUser[<string>field] = <string><unknown>app.firestore.FieldValue.delete();
      } else if (field === 'irlRoomCount') {
        partialUser[<string>field] = Number(after);
      } else {
        partialUser[<string>field] = after;
      }
    }
    if (Object.keys(changes).length === 0) {
      return;
    }
    partialUser.auditLogEntry = this.newAuditLogEntry(UserAuditLogEntryType.APPLICANT_FIELDS_EDITED, changes);
    await this.userDoc(uidToSet).update(partialUser);
  }

  async setShadowBan(uidToSet: string, on: boolean, note?: string) {
    const batch = this.db.firestore.batch();
    await this.db.collection<TerpecaRanking>('rankings').ref
    .where('year', '==', environment.currentAwardYear)
    .where('userId', '==', uidToSet).get()
    .then((snapshot: QuerySnapshot<TerpecaRanking>) => {
      snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaRanking>) => {
        batch.update(doc.ref, {
          shadowbanned: on
        });
      });
    });
    batch.update(this.userDoc(uidToSet).ref, {
      shadowbanned: on,
      auditLogEntry: this.newAuditLogEntry(on ? UserAuditLogEntryType.SHADOW_BAN_ON : UserAuditLogEntryType.SHADOW_BAN_OFF, note)
    });
    await batch.commit();
  }

  async setDisabled(uidToSet: string, on: boolean, note?: string) {
    if (!this.currentUser?.isOwner || uidToSet === this.currentUser.uid) {
      return;
    }
    const target = (await this.userDoc(uidToSet).ref.get()).data();
    if (!target || target.isOwner) {
      return;
    }
    await this.userDoc(uidToSet).update({
      disabled: on,
      auditLogEntry: this.newAuditLogEntry(
        on ? UserAuditLogEntryType.ACCOUNT_DISABLED : UserAuditLogEntryType.ACCOUNT_ENABLED, note)
    });
  }

  async setNominationPenaltyWaived(uidToSet: string, on: boolean, note?: string) {
    if (!this.currentUser?.isOwner) {
      return;
    }
    const year = environment.currentAwardYear;
    const payload: NominationPenaltyWaiverPayload = note ? { year, note } : { year };
    await this.userDoc(uidToSet).update({
      nominationPenaltyWaived: <number[]><unknown>(on ?
        app.firestore.FieldValue.arrayUnion(year) : app.firestore.FieldValue.arrayRemove(year)),
      auditLogEntry: this.newAuditLogEntry(
        on ? UserAuditLogEntryType.NOMINATION_PENALTY_WAIVED : UserAuditLogEntryType.NOMINATION_PENALTY_WAIVER_REMOVED, payload)
    });
  }

  async submitApplication(partialUser: Partial<TerpecaUser>) {
    await this.updateUser(partialUser);
    const ref = this.userDoc(this.currentUser.uid).ref;
    await ref.firestore.runTransaction(async (transaction) => {
      const snap = await transaction.get(ref);
      const data = snap.data();
      if (!data || data.status !== ApplicationStatus.NONE) {
        return;
      }
      transaction.update(ref, {
        status: ApplicationStatus.PENDING,
        auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.APPLICATION_STATUS_CHANGED,
          { status: ApplicationStatus.PENDING })
      });
    });
  }

  async reapply() {
    await this.setApplicationStatus(this.currentUser.uid, ApplicationStatus.NONE);
  }

  async setApplicationStatus(
    uidToSet: string,
    statusOrPayload: (ApplicationStatus | ApplicationResubmitRequestPayload | ApplicationDenyRequestPayload)) {
    let applyStatus: ApplicationStatus;
    let payload: any;
    if (typeof statusOrPayload === 'number') {
      applyStatus = statusOrPayload;
      payload = { status: applyStatus };
    } else {
      applyStatus = statusOrPayload.status;
      payload = statusOrPayload;
    }
    const partialUser: Partial<TerpecaUser> = {
      status: applyStatus,
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.APPLICATION_STATUS_CHANGED, payload)
    };
    if (applyStatus === ApplicationStatus.DENIED) {
      partialUser.applicationDenied = <number[]><unknown>app.firestore.FieldValue.arrayUnion(environment.currentAwardYear);
    }
    if (applyStatus >= ApplicationStatus.NOMINATOR) {
      partialUser.upgradeRequested = <boolean><unknown>app.firestore.FieldValue.delete();
    }
    if (applyStatus === ApplicationStatus.REVIEWER) {
      // We turn notifications on by default for new ambassadors; they can then turn them off if they like.
      partialUser.notifyOnNewApplicant = true;
      partialUser.notifyOnNewRoom = true;
    }
    await this.userDoc(uidToSet).update(partialUser);
  }

  async addVouch(uidToSet: string, note: string) {
    await this.userDoc(uidToSet).update({
      vouchList: <string[]><unknown>app.firestore.FieldValue.arrayUnion(this.currentUser.uid),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.VOUCH_ADDED, note)
    });
  }

  async removeVouch(uidToSet: string, note: string) {
    await this.userDoc(uidToSet).update({
      vouchList: <string[]><unknown>app.firestore.FieldValue.arrayRemove(this.currentUser.uid),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.VOUCH_REMOVED, note)
    });
  }

  async addUpgradeVouch(uidToSet: string, note: string) {
    await this.userDoc(uidToSet).update({
      upgradeVouchList: <string[]><unknown>app.firestore.FieldValue.arrayUnion(this.currentUser.uid),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.UPGRADE_VOUCH_ADDED, note)
    });
  }

  async removeUpgradeVouch(uidToSet: string, note: string) {
    await this.userDoc(uidToSet).update({
      upgradeVouchList: <string[]><unknown>app.firestore.FieldValue.arrayRemove(this.currentUser.uid),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.UPGRADE_VOUCH_REMOVED, note)
    });
  }

  async confirmNominationRules() {
    await this.firestoreDoc.update({
      nominationRulesConfirmed: <number[]><unknown>app.firestore.FieldValue.arrayUnion(environment.currentAwardYear)
    });
  }

  async confirmRankingRules() {
    await this.firestoreDoc.update({
      rankingRulesConfirmed: <number[]><unknown>app.firestore.FieldValue.arrayUnion(environment.currentAwardYear)
    });
  }

  canChangeNominationStatus(user: TerpecaUser) {
    // Owners can change anyone's nomination status while that user's nomination window is
    // open (including early access for reviewers, and to fix bad data).
    if (this.currentUser.isOwner) {
      return user.status >= ApplicationStatus.NOMINATOR && this.settings.isNominationOpen(user);
    }
    // Users can only change their own, and only if eligible to nominate.
    return user.uid === this.currentUser.uid &&
      this.settings.isNominationOpen(user) &&
      isEligibleToNominate(user, environment.currentAwardYear);
  }

  async submitNominations(nominator?: TerpecaUser) {
    const user = nominator || this.currentUser;
    if (!this.canChangeNominationStatus(user)) {
      console.log(`Unable to submit nominations for ${user.realName}`);
      return;
    }
    const batch = this.db.firestore.batch();
    await this.db.collection<TerpecaNomination>('nominations').ref
    .where('year', '==', environment.currentAwardYear)
    .where('userId', '==', user.uid).get()
    .then((snapshot: QuerySnapshot<TerpecaNomination>) => {
      snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaNomination>) => {
        batch.update(doc.ref, {
          pending: false
        });
        const nom: TerpecaNomination = doc.data();
        if (nom.roomId && !nom.disqualified) {
          const nomId: NominationId = { nominationId: doc.id, year: nom.year };
          this.rooms.addToBatch(batch, nom.roomId, {
            nominations: <NominationId[]><unknown>app.firestore.FieldValue.arrayUnion(nomId)
          });
        }
      });
    });
    batch.update(nominator ? this.userDoc(nominator.uid).ref : this.firestoreDoc.ref, {
      nominationsSubmitted: <number[]><unknown>app.firestore.FieldValue.arrayUnion(environment.currentAwardYear),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.NOMINATIONS_SUBMITTED, environment.currentAwardYear)
    });
    await batch.commit();
  }

  async reopenNominations(nominator?: TerpecaUser) {
    const user = nominator || this.currentUser;
    if (!this.canChangeNominationStatus(user)) {
      console.log(`Unable to reopen nominations for ${user.realName}`);
      return;
    }
    const batch = this.db.firestore.batch();
    await this.db.collection<TerpecaNomination>('nominations').ref
    .where('year', '==', environment.currentAwardYear)
    .where('userId', '==', user.uid).get()
    .then((snapshot: QuerySnapshot<TerpecaNomination>) => {
      snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaNomination>) => {
        batch.update(doc.ref, {
          pending: true
        });
        const nom: TerpecaNomination = doc.data();
        if (nom.roomId) {
          const nomId: NominationId = { nominationId: doc.id, year: nom.year };
          this.rooms.addToBatch(batch, nom.roomId, {
            nominations: <NominationId[]><unknown>app.firestore.FieldValue.arrayRemove(nomId)
          });
        }
      });
    });
    batch.update(nominator ? this.userDoc(nominator.uid).ref : this.firestoreDoc.ref, {
      nominationsSubmitted: <number[]><unknown>app.firestore.FieldValue.arrayRemove(environment.currentAwardYear),
      auditLogEntry: this.newAuditLogEntry(
        ((!nominator || this.currentUser.uid === nominator.uid) && !this.settings.isPastNominationDeadline()) ?
        UserAuditLogEntryType.NOMINATIONS_REOPENED :
        UserAuditLogEntryType.NOMINATIONS_REVOKED, environment.currentAwardYear)
    });
    await batch.commit();
  }

  canChangeRankingStatus(user: TerpecaUser) {
    return this.currentUser.isOwner && user.status >= ApplicationStatus.VOTER &&
      this.settings.isVotingOpen(user);
  }

  async submitRankings(voter?: TerpecaUser) {
    if (voter && !this.canChangeRankingStatus(voter)) {
      console.log(`Unable to submit rankings for ${voter.realName}`);
      return;
    }
    const batch = this.db.firestore.batch();
    let regularRanking: TerpecaRanking;
    await this.db.collection<TerpecaRanking>('rankings').ref
    .where('year', '==', environment.currentAwardYear)
    .where('userId', '==', voter ? voter.uid : this.currentUser.uid).get()
    .then((snapshot: QuerySnapshot<TerpecaRanking>) => {
      snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaRanking>) => {
        batch.update(doc.ref, {
          submitted: true
        });
        const ranking: TerpecaRanking = doc.data();
        if (ranking?.category === TerpecaCategory.TOP_ROOM) {
          regularRanking = ranking;
        }
      });
    });
    if (environment.currentAwardYear >= 2023 && regularRanking) {
      await this.db.collection<TerpecaQuote>('quotes').ref
      .where('year', '==', environment.currentAwardYear)
      .where('userId', '==', voter ? voter.uid : this.currentUser.uid).get()
      .then((snapshot: QuerySnapshot<TerpecaQuote>) => {
        snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaQuote>) => {
          const quote: TerpecaQuote = doc.data();
          if (quote && regularRanking.rankedIds?.slice(0, maxQuotesPerYear).includes(quote.roomId)) {
            batch.update(doc.ref, {
              submitted: true
            });
          }
        });
      });
    }
    batch.update(voter ? this.userDoc(voter.uid).ref : this.firestoreDoc.ref, {
      rankingsSubmitted: <number[]><unknown>app.firestore.FieldValue.arrayUnion(environment.currentAwardYear),
      auditLogEntry: this.newAuditLogEntry(UserAuditLogEntryType.RANKINGS_SUBMITTED, environment.currentAwardYear)
    });
    await batch.commit();
  }

  async reopenRankings(voter?: TerpecaUser) {
    if (voter && !this.canChangeRankingStatus(voter)) {
      console.log(`Unable to reopen rankings for ${voter.realName}`);
      return;
    }
    const batch = this.db.firestore.batch();
    await this.db.collection<TerpecaRanking>('rankings').ref
    .where('year', '==', environment.currentAwardYear)
    .where('userId', '==', voter ? voter.uid : this.currentUser.uid).get()
    .then((snapshot: QuerySnapshot<TerpecaRanking>) => {
      snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaRanking>) => {
        batch.update(doc.ref, {
          submitted: false
        });
      });
    });
    if (environment.currentAwardYear >= 2023) {
      await this.db.collection<TerpecaQuote>('quotes').ref
      .where('year', '==', environment.currentAwardYear)
      .where('userId', '==', voter ? voter.uid : this.currentUser.uid).get()
      .then((snapshot: QuerySnapshot<TerpecaQuote>) => {
        snapshot.forEach((doc: QueryDocumentSnapshot<TerpecaQuote>) => {
          batch.update(doc.ref, {
            submitted: false
          });
        });
      });
    }
    batch.update(voter ? this.userDoc(voter.uid).ref : this.firestoreDoc.ref, {
      rankingsSubmitted: <number[]><unknown>app.firestore.FieldValue.arrayRemove(environment.currentAwardYear),
      auditLogEntry: this.newAuditLogEntry(
        ((!voter || this.currentUser.uid === voter.uid) && !this.settings.isPastVotingDeadline()) ?
        UserAuditLogEntryType.RANKINGS_REOPENED :
        UserAuditLogEntryType.RANKINGS_REVOKED, environment.currentAwardYear)
    });
    await batch.commit();
  }

  userDoc(uid: string): AngularFirestoreDocument<TerpecaUser> {
    return this.db.doc<TerpecaUser>(`users/${uid}`);
  }

  newAuditLogEntry(type: UserAuditLogEntryType, data?: any): UserAuditLogEntry {
    return {
      uid: this.currentUser.uid,
      name: this.currentUser.realName || this.currentUser.displayName,
      entryType: type,
      payload: data,
      timestamp: <app.firestore.Timestamp>app.firestore.FieldValue.serverTimestamp(),
      ipAddress: AppComponent.ip4Address || '',
      ip6Address: AppComponent.ip6Address || '',
    };
  }

  logout(): void {
    this.afAuth.signOut().then(async (_res) => { await this.router.navigate(['/login']); });
  }
}
